import cn from 'classnames';
import type { LinkProps, To } from 'react-router';
import { Link } from '#components/atoms/link/Link.tsx';
import buttonStyles from '../Button.module.css';
import styles from './LinkButton.module.css';

type Props = (Omit<LinkProps, 'to'> &
  (
    | {
        /** If provided, we'll use a react-router {@link Link} */
        to: To;
        href?: undefined;
      }
    | {
        /** If provided, we'll set `reloadDocument` on the {@link Link} */
        href: string;
        to?: undefined;
      }
  )) & {
  /** The type of button this should be */
  variant?: 'primary' | 'secondary' | 'link' | 'icon';
  /** The size of the button */
  size?: 'xsmall' | 'small' | 'medium';
  /** Optional classes to apply to the button */
  className?: string;
  /** By default, we add a float:left style, this option prevents that to allow full-width */
  fullWidth?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
};

export function LinkButton({
  variant = 'primary',
  size = 'medium',
  children,
  to,
  href,
  className,
  fullWidth,
  ref,
  ...props
}: Props) {
  const classes = cn(
    'btn',
    buttonStyles.Button,
    buttonStyles[`Button___${variant}` as const],
    buttonStyles[`Button___${size}` as const],
    styles.LinkButton,
    fullWidth ? 'width-100' : styles.LinkButton___left,
    className
  );

  if (to) {
    return (
      <Link className={classes} to={to} ref={ref} {...props}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <Link className={classes} to={href} reloadDocument={true} ref={ref} {...props}>
        {children}
      </Link>
    );
  }
}
